<template>
  <div v-loading="isSavingChanges" class="share-planner-settings">
    <div class="share-planner-settings__body">
      <SharePlannerSettingsLink
        :value="link"
        class="share-planner-settings__body-entry"
      />
      <SharePlannerSettingsPasscode
        v-model:needs-reset="hasChangedPasscodeSetting"
        v-model:passcode="currentPasscode"
        class="share-planner-settings__body-entry"
        data-testid="share-planner-settings__passcode"
        @change="changeRestrictionHandler"
      />
    </div>
    <div class="share-planner-settings__footer">
      <PxButton
        ref="cancelButton"
        class="share-planner-settings__footer-link"
        size="small"
        type="link"
        @click="onClickCancelHandler"
      >
        {{ $t("common.cancel") }}
      </PxButton>
      <PxButton
        ref="saveButton"
        :disabled="!canSaveChanges || isSavingChanges"
        class="share-planner-settings__footer-button"
        type="green"
        @click="onClickSaveHandler"
      >
        {{ $t("common.saveChanges") }}
      </PxButton>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import SharePlannerSettingsLink from "@/modules/milestone-planner/components/share-planner/share-planner-settings-link.vue";
import SharePlannerSettingsPasscode from "@/modules/milestone-planner/components/share-planner/share-planner-settings-passcode.vue";

export default defineComponent({
  name: "SharePlannerSettings",

  components: {
    SharePlannerSettingsLink,
    SharePlannerSettingsPasscode,
  },

  props: {
    link: {
      type: String,
      required: true,
    },

    passcode: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      currentPasscode: "",
      canSaveChanges: false,
      hasPasscodeRestriction: false,
      hasChangedPasscodeSetting: false,
    };
  },

  computed: {
    isSavingChanges(): boolean {
      return this.$store.get("auth/milestonePlanners.loading");
    },
  },

  watch: {
    passcode(newValue: string, previousValue: string) {
      if (newValue !== this.currentPasscode || !previousValue.length) {
        this.currentPasscode = newValue;
        this.canSaveChanges = false;
      }
    },

    hasChangedPasscodeSetting(hasChanged: boolean) {
      if (!hasChanged) {
        this.canSaveChanges = hasChanged;
      }
    },
  },

  created() {
    this.currentPasscode = this.passcode;
  },

  methods: {
    onClickCancelHandler() {
      this.$emitter.emit("planner-settings", false);
      this.currentPasscode = this.passcode;
      this.hasChangedPasscodeSetting = this.canSaveChanges;
    },

    onClickSaveHandler() {
      const newPasscode = this.hasPasscodeRestriction
        ? this.currentPasscode
        : null;
      this.$emit("save", newPasscode);
      this.canSaveChanges = false;
    },

    changeRestrictionHandler(isRestricted: boolean, newPasscode: string) {
      this.hasPasscodeRestriction = isRestricted;
      this.canSaveChanges =
        (isRestricted && newPasscode !== this.passcode) ||
        (!isRestricted && !!this.passcode);
    },
  },
});
</script>

<style lang="scss" scoped>
.share-planner-settings {
  display: flex;
  flex-flow: row wrap;

  &__body {
    flex: 1 1 auto;
    min-height: 466px;
    padding: 1px 0 0 0;
  }

  &__body-entry:not(:first-child) {
    margin-top: 24px;
  }

  &__footer {
    width: 100%;
    padding: 6px 2px 2px 0;
    text-align: right;
  }

  &__footer-button {
    height: 40px;
    padding: 0 16px;
    margin-left: 11px;
  }
}
</style>
